import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap, map, share } from 'rxjs/operators';
import { ConfigurationService } from '@config/configuration.service';
import {
    GetAlgoResponse, ExecuteAlgoRequest, ExecuteAlgoResponse, SupportedIndicatorI,
    SaveAlgoResponse, AlgoListResponse, StrategySocketUpdateI, StrategyCRUDSocketMessage, ActiveTerminatedAlgo, ExternalAlgoModelI,
    CacheEmitter
} from './strategies.interface';
import { AlgoStrategy, Indicator, RunningCompletedStrategy } from './strategy.model';
import { of, Observable, pipe } from 'rxjs';
import { sortBy, find, findIndex } from 'lodash-es';
import { SymbolSearchService } from '../symbol-search';
import { WebApiStrategyRunningType, WebApiTransactionType, WebApiStrategyResponseType } from '../../../../utProto/webApiStrategyEnums_pb';
import { BooksSocketService } from '../websocket/books-socket/books-socket.service';
import { MarketSocketService, SubjectType } from '../websocket';
import { QuoteStoreService } from '../quote-store';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { TimeConfigI } from '@app/modules/admin/providers/timer-configuration/time.configuration.interfaces';
import { TimerConfig } from '@app/modules/admin/providers/timer-configuration/timer-configuration.models';
import { StrategyStatus } from '../../../../utProto/enums_pb';
import { MOMENT_DATE_FORMAT, MOMENT_COMPLETE_TIME, Utils, CustomEvents, MOMENT_TIME_FORMAT } from '@app/_helpers';
import * as moment from 'moment';
import { PortfolioService } from '@app/_shared';
import { EventsService } from '../events/events.service';
import { BriefPortfolioModel, BriefStrategyCache } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.models';
import { EXCHANGE_STRINGS } from '@app/_shared/modules/portfolio-form/portfolio-form.enums';
import { cacheMapTypes, MyStrategyType } from '@app/modules/trading/modules/my-strategies/my-strategies.consts';
import { MarketPlaceService } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.service';

@Injectable({
    providedIn: 'root'

})
export class StrategiesService {
    strategyListUpdated = new EventEmitter();
    newPorfolioDeployed = new EventEmitter();
    portfolioObjectUpdated = new EventEmitter<CacheEmitter>();

    params: Indicator[];
    allStrategies: AlgoStrategy[];
    runningCompletedStrategies: BriefPortfolioModel[] = [];
    $lisAlgoRequest: Observable<any>;
    $listActiveCompletedAlgoRequest: Observable<any>;


    timeConfiguration: TimerConfig[];

    constructor(
        private http: HttpClient,
        private confService: ConfigurationService,
        private symbolSearchService: SymbolSearchService,
        private bookSocketService: BooksSocketService,
        private quoteService: QuoteStoreService,
        private eventsService: EventsService,
        private portfolioService: PortfolioService,
        private marketSocketService: MarketSocketService,
        private marketPlaceService: MarketPlaceService,
    ) {
        // TODO: Use Cache for strategies
        this.bookSocketService.getSubject<StrategyCRUDSocketMessage>(SubjectType.LISTALGOS).subscribe((update) => {
            if (update.update) {
                try {
                    switch (update.update) {
                        case 'post':
                            this.addStrategyInCache(update.algo_body, update.algo_id);
                            this.strategyListUpdated.emit();
                            break;

                        case 'delete':
                            this.deleteStrategyFromCache(update.algo_id);
                            this.strategyListUpdated.emit(true);
                            break;

                        case 'put':
                            this.updateStrategyInCache(update.algo_id, update.algo_body);
                            this.strategyListUpdated.emit();
                            break;

                        default:
                            break;
                    }
                } catch (error) {
                    console.error('Error in updating algo state received from socket', error, update);
                }
            } else if (update.hasOwnProperty('subscribed')) {
                this.isPortfolioStateChange(update, true, update.subscribed);
            } else if (update.hasOwnProperty('bookmarked')) { 
                this.isPortfolioStateChange(update, false, update.bookmarked);       
            }
        });

        this.bookSocketService.getSubject(SubjectType.STRATEGY).subscribe((update) => {
            if (update.strategyRunningType !== WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING) {
                this.updateAlgoStateSocketHandler(update);
            }
        })

        this.eventsService.subscribe(CustomEvents.StreamReconnected, () => { this.strategyListUpdated.emit(); });
    }

    isPortfolioStateChange(update, isSubscribe: boolean, value: boolean) {
        const portfolioCached = this.marketPlaceService.portfoliosCachedByType;
        for (const type of Object.values(cacheMapTypes)) {
            if (portfolioCached.has(type)) {
                const uniquePortfolio = portfolioCached.get(type).find((portfolio) => {
                    return portfolio.portfolioID == update.algo_name;
                });
                if (uniquePortfolio) {
                    if (isSubscribe) {
                        uniquePortfolio.subscribed = value;  
                    } else {
                        uniquePortfolio.bookmarked = value;
                    } 
                    
                    this.portfolioObjectUpdated.emit({portfolio: uniquePortfolio , updatePortfolios: false});
                }
            }
        }
    }

    getIndicatorsList() {
        if (this.params) {
            return of(this.params);
        }
        return this.http.get<SupportedIndicatorI[]>(this.confService.applicationUrls.indicatorList)
            .pipe(
                map((res) => {
                    this.params = sortBy(res.map(indicator => new Indicator(indicator)), ['name']);
                    return this.params;
                })
            );
    }

    saveStrategy(strategy: AlgoStrategy) {
        return this.http.post<SaveAlgoResponse>(this.confService.applicationUrls.algoCreationUpdating, strategy.getCleanedStructure())
            .pipe(tap((saveResponse) => {
                this.addStrategyInCache(strategy, saveResponse.algo_id);
            }));
    }

    addStrategyInCache(strategy: AlgoStrategy, algo_id: string) {
        // temprory handling as we are not mainting cahche yet
        this.portfolioService.getSingleBreifPortfolio(algo_id).subscribe();

        // try {
        //     if (this.allStrategies && this.allStrategies.find(algo => algo.id === algo_id)) { return; }

        //     strategy.id = algo_id;
        //     if (!(strategy instanceof AlgoStrategy)) {
        //         const algo = new AlgoStrategy();
        //         algo.updateValues(strategy);
        //         strategy = algo;
        //     }
        //     this.allStrategies.unshift(strategy);
        //     this.strategyListUpdated.emit();
        // } catch (error) {
        //     console.log('Error in update strategy cache', error);
        // }
    }

    getStrategy(algoId: string, getWholeObject?: boolean) {
        return this.http.get<GetAlgoResponse>(this.confService.applicationUrls.algoCreationUpdating, {
            params: {
                algo_id: algoId
            }
        }).pipe(
            map((algo) => {
                if (getWholeObject) {
                    return algo;
                }
                const strategyObj = new AlgoStrategy();
                strategyObj.parseJSON(algo.algo, this.symbolSearchService);
                strategyObj.id = algo._id.$oid;
                strategyObj.algoName = algo.algoName;
                strategyObj.algoNotes = algo.algoNotes;
                strategyObj.createdAt = algo.createdAt;
                strategyObj.creationMethod = algo.creationMethod;
                strategyObj.apiID = algo.apiID;
                // By default algo cycles should be equal to 1.
                if (strategyObj.algoCycles == 0) {
                    strategyObj.algoCycles = 1;
                }
                strategyObj.setMarketPlaceSettings(algo);
                return strategyObj;
            })
        )
    }


    deleteStrategy(algoId: string) {
        return this.http.delete(this.confService.applicationUrls.algoCreationUpdating, {
            params: {
                algo_id: algoId
            }
        }).pipe(tap(
            (_) => {
                this.strategyListUpdated.emit();
            }
        ))
    }

    deleteStrategyFromCache(algoId: string) {
        return
        // remove(this.allStrategies, (algo) => algo.id === algoId);
        // this.strategyListUpdated.emit();
    }

    // Only Refresh from API when first fetch.
    getAllStrategies(refresh = false): Observable<AlgoStrategy[]> {
        if (this.allStrategies && !refresh) {
            return of(this.allStrategies);
        }

        if (this.$lisAlgoRequest) { return this.$lisAlgoRequest; }

        this.$lisAlgoRequest = this.http.get<AlgoListResponse>(this.confService.applicationUrls.listAlgos)
            .pipe(
                share(),
                map((algos) => {
                    return algos.algos.map(algo => {
                        const strategyObj = new AlgoStrategy();
                        strategyObj.parseJSON(algo.algo, this.symbolSearchService);
                        strategyObj.id = algo._id.$oid;
                        strategyObj.algoName = algo.algoName;
                        strategyObj.algoNotes = algo.algoNotes;
                        return strategyObj;
                    })
                }),
                tap(res => {
                    this.allStrategies = res;
                    this.strategyListUpdated.emit();
                })
            )
        return this.$lisAlgoRequest;
    }

    updateStrategy(algoID: string, strategy: AlgoStrategy, oldName?: string) {
        return this.http.put(this.confService.applicationUrls.algoCreationUpdating, {
            algo_id: algoID,
            algo: strategy.getCleanedStructure(),
            ...(!!oldName && { old_name: oldName })
        }).pipe(
            tap(() => {
                this.updateStrategyInCache(algoID, strategy);
            })
        )
    }

    updateStrategyInCache(algoID: string, strategy: AlgoStrategy) {
        return
        // try {
        //     find(this.allStrategies, algo => algo.id === algoID).updateValues(strategy);
        //     this.strategyListUpdated.emit();
        // } catch (error) {
        //     console.error('Error in updating strategy in cache', error);
        // }
    }


    pauseAllStrategies(refresh = false) {
        // this.getRunningStrategies().subscribe((res: RunningCompletedStrategy[]) => {
        //     if (!res.length) {
        //         this.snackbar.open(NO_RUNNING_STRATEGIES);
        //     }
        //     res.forEach(strategy => {
        //         const req: any = {
        //             algo_id: strategy.UniqueAlgoName,
        //             state: WebApiTransactionType.WEBAPITRANSACTIONTYPE_PAUSE
        //         }

        //         this.updateAlgoState(req).subscribe(res => {
        //             this.snackbar.open(res.message);
        //         })
        //     })
        // })

        return this.updateAllStrategies({ state: WebApiTransactionType.WEBAPITRANSACTIONTYPE_PAUSE }).subscribe();
    }

    updateAlgoState(request) {
        return this.http.post<ExecuteAlgoResponse>(this.confService.applicationUrls.updateAlgoState, request)
    }


    updateAllStrategies(state) {
        return this.http.post<ExecuteAlgoResponse>(this.confService.applicationUrls.updateStateForAllAlgos, state);
    }

    updateAlgoStateSocketHandler(update: StrategySocketUpdateI & StrategyCRUDSocketMessage) {
        const transactionType = update.webApiStrategyResponseType

        // If strategy has a unique name (unique portfolio ID), we add that ID in cache as well
        if (update?.algoName && update?.uniqueAlgoName)
            BriefStrategyCache.Instance.updateUniqueIdByPortfolioID(update.algoName, update.uniqueAlgoName);

        switch (transactionType) {
            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_TERMINATE_SUCCESS:
                const runningOutLier = this.runningCompletedStrategies.find(el => el.uniquePortfolioID === update.uniqueAlgoName);
                if (runningOutLier) {
                    runningOutLier.status = StrategyStatus.STRATEGYSTATUS_TERMINATED;
                }
                this.updatePortfoliosData(update, StrategyStatus.STRATEGYSTATUS_TERMINATED);
                break;

            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_FORCE_TERMINATE_SUCCESS:
                const forceTerminatedOutLier = this.runningCompletedStrategies.find(el => el.uniquePortfolioID === update.uniqueAlgoName);
                if (forceTerminatedOutLier) {
                    forceTerminatedOutLier.status = StrategyStatus.STRATEGYSTATUS_FORCE_TERMINATED;
                }
                this.updatePortfoliosData(update, StrategyStatus.STRATEGYSTATUS_FORCE_TERMINATED);
                break;

            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_SUCCESSFUL:
                this.updatePortfoliosData(update, StrategyStatus.STRATEGYSTATUS_RUNNING);
                this.newPorfolioDeployed.emit();
                break;

            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_PAUSE_SUCCESSFUL:
                const outLier = this.runningCompletedStrategies.find(el => el.uniquePortfolioID === update.uniqueAlgoName);
                if (outLier) {
                    outLier.status = StrategyStatus.STRATEGYSTATUS_PAUSED;
                }
                this.updatePortfoliosData(update, StrategyStatus.STRATEGYSTATUS_PAUSED);
                break;

            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_RUN_SUCCESSFUL:
                const pausedOutLier = this.runningCompletedStrategies.find(el => el.uniquePortfolioID === update.uniqueAlgoName);
                if (pausedOutLier) {
                    pausedOutLier.status = StrategyStatus.STRATEGYSTATUS_RUNNING;
                }
                this.updatePortfoliosData(update, StrategyStatus.STRATEGYSTATUS_RUNNING);
                break;

            case WebApiStrategyResponseType.WEBAPISTRATEGYRESPONSE_STRATEGY_WAIT:
                const pausedWaitingOutLier = this.runningCompletedStrategies.find(el => el.uniquePortfolioID === update.uniqueAlgoName);
                if (pausedWaitingOutLier) {
                    pausedWaitingOutLier.status = StrategyStatus.STRATEGYSTATUS_AWAIT;
                }
                break;

        }
        this.eventsService.publish(CustomEvents.PortfolioStatusEvent, update);
        
    }

    updatePortfoliosData(update, status:number) {
        const portfolioCached = this.marketPlaceService.portfoliosCachedByType;
        if (update.uniqueAlgoName && update.childStrategyId === "") {
            for (const type of Object.values(cacheMapTypes)) {
                if (portfolioCached.has(type)) {
                    const uniquePortfolio = portfolioCached.get(type).find((portfolio) => {
                        return type === cacheMapTypes.Deployed ? portfolio.uniquePortfolioID === update.uniqueAlgoName : portfolio.portfolioID == update.algoName;
                    });
                    if (uniquePortfolio) {
                        uniquePortfolio?.updatePortfolioState(status);
                        uniquePortfolio.status = status;
                        uniquePortfolio.multiplier = update.multiplier;
                        uniquePortfolio.portfolioName = update.portfolioName;
                        uniquePortfolio.margin_required = update.margin_required || (uniquePortfolio.minimumCapital * update.multiplier);
                        if (update.uniqueAlgoName) {
                            uniquePortfolio.uniquePortfolioID = update.uniqueAlgoName;
                        }
                        // In case Portfolio is not obtained from Deployed cache, deployed_on is not present
                        if(isNaN(uniquePortfolio.deployed_on) && update.deployed_on) {
                            uniquePortfolio.deployed_on = Utils.convertTimeInMilliseconds(update.deployed_on);
                        }
                        this.portfolioObjectUpdated.emit({portfolio: uniquePortfolio , updatePortfolios: true, status: status, type: type});
                    }
                }
            }
        } else {
            if (portfolioCached.has(cacheMapTypes.Deployed)) {
                const uniquePortfolio = portfolioCached.get(cacheMapTypes.Deployed).find((portfolio) => {
                    return portfolio.uniquePortfolioID === update.childParentId;
                });
                if (uniquePortfolio) {
                    uniquePortfolio.StrategyDetailKey[update.algoName].Status = status;
                    this.portfolioObjectUpdated.emit({portfolio: uniquePortfolio , updatePortfolios: true , status: status});
                }
               
            }
        }
    }

    mapKeys(request: any, backTestDeploy: boolean) {
        const tempName = `Backtest_${moment().format(MOMENT_DATE_FORMAT + '-' + MOMENT_COMPLETE_TIME)}`;
        const obj = new RunningCompletedStrategy(request.algo_obj, this.symbolSearchService);
        request.algo_obj = obj.mapToAlgoStrategy(this.symbolSearchService, backTestDeploy)
        if (request.algo_obj.algoName === 'NA') {
            request.algo_obj.algoName = tempName;
        }
        return request;
    }

    sendStrategyFileLoadMessage(fileName: string): Promise<number> {
        return new Promise((res, rej) => {
            this.bookSocketService.getSubject(SubjectType.STRATEGY).subscribe((update) => {
                if (update?.strategyModuleName === fileName) {
                    if (update.apiId > -1) {
                        res(update.apiId)
                    } else {
                        rej(-1);
                    }
                }
            });

            this.loadStrategy(fileName).subscribe();
        })
    }

    executeAlgo(request: ExecuteAlgoRequest, backTestDeploy = false) {
        let req = request;
        if (request.algo_type === WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING) {
            this.bookSocketService.getSubject(SubjectType.BACKTEST).next(request.backTestData)
        }
        if (backTestDeploy) {
            req = this.mapKeys(request, backTestDeploy)
        }
        return this.http.post<ExecuteAlgoResponse[]>(this.confService.applicationUrls.algoExecution, req).pipe(
            tap(res => {
                const algo = find(this.allStrategies, algo => algo.id === request.algo_id);
                if (request.state === WebApiTransactionType.WEBAPITRANSACTIONTYPE_NEW && algo && !algo.algoIDs) {
                    (request.symbols || algo.symbols).forEach((symbol, responseIndex) => {
                        symbol['token_id'] = Utils.getRandomTokenId();
                        const idx = findIndex(request.symbols || algo.symbols,
                            symbolItr => symbolItr.market_id === symbol.market_id && symbolItr.security_id === symbol.security_id
                        );
                    })
                }
                this.strategyListUpdated.emit();
            })
        );
    }

    terminateAllStrategies() {
        // this.getActiveAlgosNew().subscribe((res: RunningCompletedStrategy[]) => {
        //     if (!res.length) {
        //         this.snackbar.open(NO_RUNNING_STRATEGIES);
        //     }
        //     res.forEach(row => {
        //         this.terminateStrategy(row);
        //     });
        // });

        return this.updateAllStrategies({ state: WebApiTransactionType.WEBAPITRANSACTIONTYPE_CANCEL }).subscribe();


    }

    unsubscribeQuotes(algo: ActiveTerminatedAlgo) {
        this.marketSocketService.unSubscribeSymbol(
            { market_id: MarketIDMap[algo.StrategyParams.symbol.market_id], security_id: algo.StrategyParams.symbol.security_id }
        );
    }

    isAlgoNameUnique(name: string) {
        return this.getAllStrategies().pipe(
            map(res => {
                const allAlgo = res.filter(s => s.algoName == name);
                return allAlgo.length === 0;
            })
        )
    }

    isAlgoNameUniqueSync(name: string) {
        /*
            As this is run dynamically even before component's initiation, need to add validations
            to prevent console errors.
            If the algo name exists in the stored strategies, the algo name is not unique.
        */
        return find(this.allStrategies, s => s.algoName === name) ? false : true;
    }



    detectParamsFromText(text: string) {
        return this.http.get(this.confService.applicationUrls.paramsDetection, {
            params: {
                strategyText: text
            }
        })
    }

    getTimeConf(): Observable<TimerConfig[]> {
        if (this.timeConfiguration) {
            return of(this.timeConfiguration);
        }
        return this.http.get<TimeConfigI[]>(this.confService.applicationUrls.timeConfiguration)
            .pipe(
                map(confs => {
                    return confs.map(c => new TimerConfig(c));
                }),
                tap(res => {
                    this.timeConfiguration = res;
                    // update the quote service with the time configuration so that it can be used in picking the ltp and close price.
                    this.quoteService.timeConfiguration = res;
                })
            );
    }

    saveExternalStrategy(strategy: ExternalAlgoModelI) {
        return this.http.post<SaveAlgoResponse>(this.confService.applicationUrls.algoCreationUpdating, strategy);
    }

    updateExternalStrategy(algoID: string, strategy: ExternalAlgoModelI, oldName: string) {
        return this.http.put(this.confService.applicationUrls.algoCreationUpdating, {
            algo_id: algoID,
            algo: strategy,
            ...(!!oldName && { old_name: oldName })
        })
    }

    loadStrategy(soFileName: string) {
        return this.http.post(this.confService.applicationUrls.loadStrategy, {
            so_file_name: soFileName
        })
    }

    // Check if current time is before exchange end time
    isBeforeMarketClose(): Observable<boolean> {
        return this.getTimeConf().pipe(
            map(res => {
                const confTime = res.find(s => s.market === EXCHANGE_STRINGS[4]);
                if (confTime) {
                    return Utils.isBeforeCurrentTime(confTime.end_time, MOMENT_TIME_FORMAT);
                }
                return false;
            })
        );
    }
}
